import React, {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import useMe from '../../../hooks/useMe';
import {Glum} from '../../../puddleglum';
import PatientSelectionModal from './PatientSelectionModal';
import {
	Avatar,
	Box,
	Button,
	Heading,
	HStack,
	Progress,
	SimpleGrid,
	Text,
	VStack,
} from 'components/ui/chakra';
import MyProvidersController from 'puddleglum/api/patient/MyProvidersController';

type BookableProvider = Glum.Models.Provider & {
	price: Glum.Models.Price;
	is_direct_scheduling_enabled: boolean;
};

const MyProviders = () => {
	const navigate = useNavigate();
	const {me} = useMe();
	const [pendingProvider, setPendingProvider] = useState<BookableProvider | null>(null);
	const [isPatientSelectionModalOpen, setIsPatientSelectionModalOpen] = useState(false);

	const {data: providers, isLoading} = useQuery(
		['my-providers', me?.id],
		async () => {
			if (!me) return [];

			const reply = await MyProvidersController.index({
				patient_id: me.id,
			});

			return reply.data;
		},
		{
			enabled: !!me,
		},
	);

	const navigateToBooking = (provider: BookableProvider, patientId: number) => {
		const organizationId = provider.organizations![0].id;
		const locationId = provider.locations![0].id;
		const treatmentGroupId = provider.price.treatment_group_id;

		if (provider.is_direct_scheduling_enabled) {
			const params = new URLSearchParams({
				organization_id: String(organizationId),
				location_id: String(locationId),
				provider_id: String(provider.id),
				patient_id: String(patientId),
			});
			if (treatmentGroupId) {
				params.append('treatment_group_ids[]', String(treatmentGroupId));
			}
			params.set('back_url', window.location.href);
			window.location.href = `/direct-booking?${params.toString()}`;
		} else {
			navigate('/booking-request', {
				state: {
					organization_id: organizationId,
					location_id: locationId,
					provider_id: provider.id,
					treatment_group_ids: [treatmentGroupId],
					patient_id: patientId,
				},
			});
		}
	};

	const openBookingRequest = (provider: BookableProvider) => {
		if (!me) return;

		if ((me.dependents?.length ?? 0) > 0) {
			setPendingProvider(provider);
			setIsPatientSelectionModalOpen(true);
			return;
		}

		navigateToBooking(provider, me.id);
	};

	const handlePatientSelect = (patientId: number) => {
		setIsPatientSelectionModalOpen(false);
		if (pendingProvider) {
			navigateToBooking(pendingProvider, patientId);
			setPendingProvider(null);
		}
	};

	const handlePatientSelectionClose = () => {
		setIsPatientSelectionModalOpen(false);
		setPendingProvider(null);
	};

	const getOrganizationName = (
		organization: Glum.Models.Organization,
		locations: Glum.Models.Location[],
	) => {
		const locationWithOrgName = locations.find((loc) => !!loc.organization_alias);
		return locationWithOrgName ? locationWithOrgName.organization_alias : organization.name;
	};

	if (!me) {
		return <Progress size='xs' isIndeterminate />;
	}

	if (isLoading || !providers) {
		return <Progress size='xs' isIndeterminate />;
	}

	if (providers.length === 0) {
		return null;
	}

	return (
		<Box w='full'>
			<Heading mb={2} size='md' color='gray.500'>
				My Providers
			</Heading>
			<SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
				{providers.map((provider) => (
					<Box
						key={provider.id}
						w='full'
						bgColor='white'
						border='solid 1px'
						borderColor='gray.200'
						borderRadius='lg'
						p={4}
					>
						<HStack spacing={4} align='start' mb={3}>
							<Avatar name={provider.name} src={provider.photo_url!} />
							<VStack align='start' flex={1} spacing={1}>
								<Text fontWeight='bold'>{provider.name}</Text>
								{provider.organizations?.map((organization) => (
									<Text key={organization.id} color='gray.500' noOfLines={1}>
										{getOrganizationName(
											organization,
											provider.locations ?? [],
										)}
									</Text>
								))}
								{provider.locations?.map((location) => (
									<Text key={location.id} color='gray.500' noOfLines={1}>
										{location.name}
									</Text>
								))}
								<Text color='gray.700' fontSize='sm' noOfLines={1}>
									Last seen: {provider.last_seen}
								</Text>
							</VStack>
						</HStack>
						<Button
							colorScheme='blue'
							w='full'
							onClick={() => openBookingRequest(provider)}
						>
							Book Now
						</Button>
					</Box>
				))}
			</SimpleGrid>
			<PatientSelectionModal
				isOpen={isPatientSelectionModalOpen}
				onClose={handlePatientSelectionClose}
				onSelect={handlePatientSelect}
				me={me}
			/>
		</Box>
	);
};

export default MyProviders;
